import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Navbar } from "../components/Navbar";
import {
  Mail, Play, Clock, CheckCircle, XCircle,
  RefreshCw, ChevronDown, ChevronUp, Users, Zap, X,
  BarChart3, Shield, Truck, Newspaper, Settings, Search, Edit3, Eye, Save
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_CONFIG = {
  reports: { label: "Reports", color: "#2563eb", icon: BarChart3 },
  finance: { label: "Finance", color: "#16a34a", icon: Settings },
  hr: { label: "HR & Compliance", color: "#9333ea", icon: Shield },
  sales: { label: "Sales & CRM", color: "#ea580c", icon: Zap },
  operations: { label: "Operations", color: "#0891b2", icon: Mail },
  transport: { label: "Transport", color: "#dc2626", icon: Truck },
  newsletter: { label: "Newsletter", color: "#c026d3", icon: Newspaper },
  system: { label: "System", color: "#64748b", icon: Settings },
};

const TEMPLATE_PREVIEWS = {
  weekly_offers_orders_report: { subject: "Weekly Offers & Orders Report - Week {date}", body: "Europacific branded HTML report with:\n- Offers by day + company breakdown\n- PF (orders) count by status\n- Customer reactivation stats\n- Voucher metrics table", sample: "blue header, tables with daily offer counts, PF status cards, reactivation funnel" },
  weekly_shipment_reports: { subject: "Weekly Shipment Report - {customer}", body: "Hapag-Lloyd inspired format:\n- Active shipments with status\n- Completed shipments\n- Historical shipment archive\n- Per-customer branded HTML", sample: "White header with logo, status table (green/yellow/red), shipment details" },
  weekly_news_summary: { subject: "Industry News Digest - Week of {date}", body: "Compiled shipping/logistics news:\n- Top headlines with links\n- Brief summaries per article\n- Industry sector breakdown", sample: "News digest format with article cards" },
  soa_monday_emails: { subject: "Statement of Account - {company}", body: "SOA email with:\n- Outstanding invoice list\n- Amount due by currency\n- Payment details\n- Due date highlights", sample: "Finance template with invoice table, red overdue badges" },
  overdue_wednesday_emails: { subject: "Overdue Payment Reminder - {company}", body: "Overdue reminder with:\n- List of overdue invoices\n- Days overdue per invoice\n- Total outstanding amount\n- Payment instructions", sample: "Red-accented template with overdue table" },
  hrbot_heartbeat_check: { subject: "[HRBot OPOZORILO] {count} agent(ov) OFFLINE", body: "Slovenian alert email:\n- List of offline agents (name, email, last seen)\n- Hours since last heartbeat\n- Escalation list for cases >48h", sample: "Red-themed HTML, agent list, escalation table" },
  compliance_daily_check: { subject: "Compliance Review Reminder - {doc_name}", body: "Reminder tiers:\n- 90-day notice\n- 30-day notice\n- 7-day urgent\n- Overdue escalation → HRBot case", sample: "Yellow/red badges by urgency, document details" },
  leave_handover_alerts: { subject: "Handover Alert: {name} on {leave_type} - {count} tasks", body: "Alert email to department:\n- Staff member on leave\n- Leave dates and type\n- List of unhandled PFs\n- Action request for colleagues", sample: "Red header, PF list in code block, department info" },
  birthday_emails: { subject: "Happy Birthday, {name}! - From Europacific", body: "Two templates:\n1. Customer: Purple gradient header, cake emoji, personalized\n2. Staff: Pink/coral header, balloons, + all@europacific.com notification", sample: "Gradient email with emoji, warm tone" },
  draft_quote_reminders: { subject: "Draft Quote Reminder - {quote_number}", body: "Reminder for:\n- Old draft quotes (>X days)\n- Quotes with scheduled reminder dates\n- Auto-generated and manual reminders", sample: "Simple notification with quote details, CTA button" },
  customer_reengagement: { subject: "Re-engagement: {stage}", body: "3-stage drip:\n- Stage 1: Check-in (no voucher)\n- Stage 2: Voucher email (7 days later)\n- Stage 3: Cold alert to staff (14 days later)", sample: "Green CTA buttons, voucher card design" },
  crm_campaign_automation: { subject: "Campaign: {campaign_name}", body: "CRM drip campaigns:\n- Day 14: First reminder\n- Day 30: Second reminder\n- Bounce processing\n- Inactive contact marking", sample: "Marketing template with CTA" },
  satisfaction_survey_auto: { subject: "How was your experience? - {pf_number}", body: "Survey email:\n- Dark blue branded header\n- Rate Your Experience CTA button\n- Links to survey landing page\n- 1-5 star rating + free text feedback", sample: "Blue branded, centered CTA button" },
  pf_creation_notification: { subject: "Europacific - Position Folder {pf_number}", body: "PF creation notice:\n- PF details and route\n- Customer notification\n- CC to notification_emails\n- Accounting copy to acc@europacific.com", sample: "Professional notification with PF summary" },
  milestone_notification: { subject: "Shipment Update - {pf_number}: {milestone}", body: "Milestone update:\n- Milestone type and date\n- Shipment reference details\n- myEuropacific tracking promotion\n- Company signature", sample: "Status update card with green promotion section" },
  trinet_customs_notification: { subject: "SI.AIS2 - {status_name} / {status_name_en}", body: "7 bilingual templates (SL + EN):\n- Import procedure started\n- Documentary check\n- Physical inspection\n- Goods released\n- Export exit confirmed\n- Transit authorization\n- Transit departure", sample: "Bilingual format: Slovenian first, separator, English below" },
  transport_order_emails: { subject: "Transport Order {to_number} - {type}", body: "TO emails:\n- Order confirmation with PDF\n- Dispatch notice\n- Status query\n- POD request\n- Loading details", sample: "Professional TO email with PDF attachment" },
  road_closure_newsletter: { subject: "HGV Driving Bans - {month} {year}", body: "AI-generated newsletter:\n- Countries: SI, HR, SK, HU, CZ, AT, IT\n- Individual dates with time windows\n- Logistics-focused tone\n- Easter-derived dates included", sample: "Branded newsletter with country sections, date tables" },
  offer_followup_drip: { subject: "Follow-up: {offer_subject}", body: "Drip stages:\n- Day 3: Offer reminder\n- Near expiry: Expiry warning\n- After rejection: Reason request\n- After acceptance: Satisfaction survey\n- Day 30: Re-engagement", sample: "Clean follow-up email with CTA" },
  email_auto_reply: { subject: "Re: {original_subject} [Ticket #{number}]", body: "Auto-reply:\n- Ticket number assignment\n- Acknowledgment of receipt\n- CC to supervisor\n- Smart filtering (skips system emails)", sample: "Simple acknowledgment with ticket reference" },
  daily_system_health_check: { subject: "System Health Report - {date}", body: "Health diagnostics:\n- Service status checks\n- Database connectivity\n- API response times\n- Scheduler timestamps\n- Error counts", sample: "Dashboard-style report with green/red indicators" },
};

function StatusBadge({ status }) {
  const config = {
    success: { bg: "#dcfce7", color: "#166534", label: "OK" },
    error: { bg: "#fee2e2", color: "#991b1b", label: "Error" },
    never: { bg: "#f1f5f9", color: "#64748b", label: "Never Run" },
  };
  const c = config[status] || config.never;
  return <span style={{ background: c.bg, color: c.color, padding: "2px 10px", borderRadius: 12, fontSize: 12, fontWeight: 600 }} data-testid={`status-badge-${status}`}>{c.label}</span>;
}

function CategoryBadge({ category }) {
  const cat = CATEGORY_CONFIG[category] || { label: category, color: "#64748b" };
  return <span style={{ background: cat.color + "18", color: cat.color, padding: "2px 10px", borderRadius: 12, fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>{cat.label}</span>;
}

function TimeAgo({ isoStr }) {
  if (!isoStr) return <span style={{ color: "#94a3b8", fontSize: 13 }}>Never</span>;
  const d = new Date(isoStr);
  const now = new Date();
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return <span style={{ fontSize: 13 }}>{diff}s ago</span>;
  if (diff < 3600) return <span style={{ fontSize: 13 }}>{Math.floor(diff / 60)}m ago</span>;
  if (diff < 86400) return <span style={{ fontSize: 13 }}>{Math.floor(diff / 3600)}h ago</span>;
  return <span style={{ fontSize: 13 }}>{Math.floor(diff / 86400)}d ago</span>;
}

/* ============ DETAIL MODAL ============ */
function RuleDetailModal({ rule, onClose, onSave, token }) {
  const [editSchedule, setEditSchedule] = useState(rule.schedule || "");
  const [editCron, setEditCron] = useState(rule.schedule_cron || "");
  const [editRecipients, setEditRecipients] = useState((rule.recipients || []).join("\n"));
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [activeTab, setActiveTab] = useState("template");

  const catConf = CATEGORY_CONFIG[rule.category] || { color: "#64748b", label: rule.category };
  const tpl = TEMPLATE_PREVIEWS[rule.id] || {};

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/email-rules/${rule.id}/history?limit=15`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) { const d = await res.json(); setHistory(d.history || []); }
      } catch (e) { console.error(e); }
      setLoadingHistory(false);
    })();
  }, [rule.id, token]);

  const handleSave = async () => {
    setSaving(true);
    const body = {};
    if (editSchedule !== rule.schedule) body.schedule = editSchedule;
    if (editCron !== rule.schedule_cron) body.schedule_cron = editCron;
    const newRecipients = editRecipients.split("\n").map(r => r.trim()).filter(Boolean);
    if (JSON.stringify(newRecipients) !== JSON.stringify(rule.recipients)) body.recipients = newRecipients;
    if (Object.keys(body).length > 0) {
      await onSave(rule.id, body);
    }
    setSaving(false);
    setEditing(false);
  };

  const tabs = [
    { id: "template", label: "Template" },
    { id: "schedule", label: "Schedule & Recipients" },
    { id: "history", label: "History" },
  ];

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center" }} data-testid="rule-detail-modal">
      <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.4)" }} onClick={onClose} />
      <div style={{ position: "relative", background: "white", borderRadius: 12, width: "min(750px, 95vw)", maxHeight: "85vh", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}>
        {/* Header */}
        <div style={{ padding: "20px 24px 0", borderBottom: "1px solid #e2e8f0" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                <span style={{ width: 10, height: 10, borderRadius: 5, background: rule.enabled ? "#22c55e" : "#d1d5db", display: "inline-block" }} />
                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#0f172a" }}>{rule.name}</h2>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <CategoryBadge category={rule.category} />
                <span style={{ fontSize: 12, color: "#64748b" }}>{rule.trigger_type === "event" ? "Event-driven" : rule.schedule}</span>
              </div>
            </div>
            <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", padding: 4 }} data-testid="close-modal-btn"><X size={20} color="#94a3b8" /></button>
          </div>
          <p style={{ fontSize: 13, color: "#64748b", margin: "0 0 12px" }}>{rule.description}</p>
          {rule.note && <p style={{ fontSize: 12, color: "#f59e0b", margin: "0 0 12px", fontStyle: "italic" }}>{rule.note}</p>}
          {/* Tabs */}
          <div style={{ display: "flex", gap: 0 }}>
            {tabs.map(t => (
              <button key={t.id} onClick={() => setActiveTab(t.id)} data-testid={`tab-${t.id}`}
                style={{ padding: "8px 20px", fontSize: 13, fontWeight: activeTab === t.id ? 600 : 400, color: activeTab === t.id ? "#2563eb" : "#64748b", background: "none", border: "none", borderBottom: activeTab === t.id ? "2px solid #2563eb" : "2px solid transparent", cursor: "pointer", marginBottom: -1 }}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: "20px 24px", overflowY: "auto", flex: 1 }}>
          {/* TEMPLATE TAB */}
          {activeTab === "template" && (
            <div>
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>Template Name</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: "#1e293b" }}>{rule.template_name || "N/A"}</div>
              </div>
              {tpl.subject && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>Subject Line</div>
                  <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#334155", fontFamily: "monospace" }}>{tpl.subject}</div>
                </div>
              )}
              {tpl.body && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>Email Content Structure</div>
                  <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "14px", fontSize: 13, color: "#334155", whiteSpace: "pre-wrap", lineHeight: 1.6 }}>{tpl.body}</div>
                </div>
              )}
              {tpl.sample && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>Visual Style</div>
                  <div style={{ background: `${catConf.color}08`, border: `1px solid ${catConf.color}30`, borderRadius: 8, padding: "10px 14px", fontSize: 12, color: catConf.color, fontStyle: "italic" }}>
                    <Eye size={14} style={{ marginRight: 6, verticalAlign: "middle" }} />{tpl.sample}
                  </div>
                </div>
              )}
              {!tpl.subject && !tpl.body && (
                <div style={{ textAlign: "center", padding: 30, color: "#94a3b8" }}>
                  <Mail size={32} style={{ marginBottom: 8, opacity: 0.4 }} />
                  <div style={{ fontSize: 13 }}>Template preview not available for this rule type</div>
                </div>
              )}
              {/* Stats row */}
              <div style={{ display: "flex", gap: 20, marginTop: 16, padding: "12px 0", borderTop: "1px solid #e2e8f0" }}>
                <div><span style={{ fontSize: 12, color: "#64748b" }}>Total runs: </span><strong style={{ color: "#1e293b" }}>{rule.total_runs || 0}</strong></div>
                <div><span style={{ fontSize: 12, color: "#16a34a" }}>Success: </span><strong style={{ color: "#16a34a" }}>{rule.total_success || 0}</strong></div>
                <div><span style={{ fontSize: 12, color: "#dc2626" }}>Failures: </span><strong style={{ color: "#dc2626" }}>{rule.total_failures || 0}</strong></div>
                <div><span style={{ fontSize: 12, color: "#64748b" }}>Last run: </span><strong><TimeAgo isoStr={rule.last_run} /></strong></div>
              </div>
            </div>
          )}

          {/* SCHEDULE TAB */}
          {activeTab === "schedule" && (
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 1 }}>Schedule Configuration</div>
                {!editing ? (
                  <button onClick={() => setEditing(true)} style={{ display: "flex", alignItems: "center", gap: 4, background: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: 6, padding: "6px 12px", fontSize: 12, cursor: "pointer", fontWeight: 500 }} data-testid="edit-schedule-btn">
                    <Edit3 size={13} /> Edit
                  </button>
                ) : (
                  <div style={{ display: "flex", gap: 6 }}>
                    <button onClick={() => setEditing(false)} style={{ padding: "6px 12px", fontSize: 12, border: "1px solid #e2e8f0", borderRadius: 6, background: "white", cursor: "pointer" }}>Cancel</button>
                    <button onClick={handleSave} disabled={saving} style={{ display: "flex", alignItems: "center", gap: 4, padding: "6px 12px", fontSize: 12, border: "none", borderRadius: 6, background: "#2563eb", color: "white", cursor: "pointer", fontWeight: 500 }} data-testid="save-schedule-btn">
                      <Save size={13} /> {saving ? "Saving..." : "Save"}
                    </button>
                  </div>
                )}
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>Schedule Description</label>
                  {editing ? (
                    <input value={editSchedule} onChange={e => setEditSchedule(e.target.value)} style={{ width: "100%", padding: "8px 12px", border: "1px solid #3b82f6", borderRadius: 6, fontSize: 13, outline: "none" }} data-testid="schedule-input" />
                  ) : (
                    <div style={{ padding: "8px 12px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 13 }}>{rule.schedule}</div>
                  )}
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>Cron Expression</label>
                  {editing ? (
                    <input value={editCron} onChange={e => setEditCron(e.target.value)} style={{ width: "100%", padding: "8px 12px", border: "1px solid #3b82f6", borderRadius: 6, fontSize: 13, fontFamily: "monospace", outline: "none" }} data-testid="cron-input" />
                  ) : (
                    <div style={{ padding: "8px 12px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 13, fontFamily: "monospace" }}>{rule.schedule_cron || "N/A"}</div>
                  )}
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>Frequency</label>
                  <div style={{ padding: "8px 12px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 13 }}>{rule.frequency || "N/A"}</div>
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>Trigger Type</label>
                  <div style={{ padding: "8px 12px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 13 }}>{rule.trigger_type || "N/A"}</div>
                </div>
              </div>

              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>
                  <Users size={13} style={{ marginRight: 4, verticalAlign: "middle" }} />Recipients ({(editing ? editRecipients.split("\n").filter(Boolean) : rule.recipients || []).length})
                </label>
                {editing ? (
                  <textarea value={editRecipients} onChange={e => setEditRecipients(e.target.value)} rows={5}
                    style={{ width: "100%", padding: "8px 12px", border: "1px solid #3b82f6", borderRadius: 6, fontSize: 13, fontFamily: "monospace", resize: "vertical", outline: "none" }}
                    placeholder="One recipient per line" data-testid="recipients-textarea" />
                ) : (
                  <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 6, padding: "10px 14px" }}>
                    {(rule.recipients || []).map((r, i) => (
                      <div key={i} style={{ fontSize: 13, padding: "2px 0", color: "#334155" }}>
                        <Users size={12} style={{ marginRight: 6, verticalAlign: "middle", color: "#94a3b8" }} />{r}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* HISTORY TAB */}
          {activeTab === "history" && (
            <div>
              {loadingHistory ? (
                <div style={{ textAlign: "center", padding: 30, color: "#94a3b8" }}>Loading history...</div>
              ) : history.length === 0 ? (
                <div style={{ textAlign: "center", padding: 30, color: "#94a3b8" }}>
                  <Clock size={28} style={{ marginBottom: 8, opacity: 0.4 }} />
                  <div style={{ fontSize: 13 }}>No execution history yet</div>
                </div>
              ) : (
                <div>
                  {history.map((h, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 0", borderBottom: "1px solid #f1f5f9" }}>
                      {h.status === "success" ? <CheckCircle size={16} style={{ color: "#16a34a", flexShrink: 0, marginTop: 2 }} /> : <XCircle size={16} style={{ color: "#dc2626", flexShrink: 0, marginTop: 2 }} />}
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, color: "#1e293b", fontWeight: 500 }}>
                          {new Date(h.executed_at).toLocaleString("sl-SI", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                          <span style={{ marginLeft: 8, fontSize: 11, color: h.status === "success" ? "#16a34a" : "#dc2626", fontWeight: 600, textTransform: "uppercase" }}>{h.status}</span>
                        </div>
                        {h.detail && <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>{h.detail}</div>}
                        {(h.sent_count > 0 || h.failed_count > 0) && (
                          <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>Sent: {h.sent_count || 0} | Failed: {h.failed_count || 0}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============ RULE ROW ============ */
function RuleRow({ rule, onToggle, onTrigger, onOpenDetail }) {
  const [triggering, setTriggering] = useState(false);
  const catConf = CATEGORY_CONFIG[rule.category] || { color: "#64748b" };

  const handleTrigger = async (e) => {
    e.stopPropagation();
    setTriggering(true);
    await onTrigger(rule.id);
    setTriggering(false);
  };

  return (
    <div
      onClick={() => onOpenDetail(rule)}
      style={{ borderLeft: `4px solid ${catConf.color}`, background: "white", borderRadius: 8, marginBottom: 8, boxShadow: "0 1px 3px rgba(0,0,0,0.06)", cursor: "pointer", transition: "box-shadow 0.15s" }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.1)"}
      onMouseLeave={e => e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.06)"}
      data-testid={`rule-row-${rule.id}`}
    >
      <div style={{ display: "grid", gridTemplateColumns: "1fr 140px 180px 100px 90px 70px", alignItems: "center", padding: "14px 18px", gap: 12 }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: 14, color: "#1e293b", marginBottom: 2 }}>{rule.name}</div>
          <div style={{ fontSize: 12, color: "#64748b", lineHeight: 1.4 }}>{rule.description?.substring(0, 100)}{rule.description?.length > 100 ? "..." : ""}</div>
          {rule.note && <div style={{ fontSize: 11, color: "#f59e0b", marginTop: 2, fontStyle: "italic" }}>{rule.note}</div>}
        </div>
        <div><CategoryBadge category={rule.category} /></div>
        <div style={{ fontSize: 12, color: "#475569" }}><Clock size={12} style={{ marginRight: 4, verticalAlign: "middle" }} />{rule.schedule}</div>
        <div>
          <StatusBadge status={rule.last_run_status || "never"} />
          <div style={{ marginTop: 2 }}><TimeAgo isoStr={rule.last_run} /></div>
        </div>
        <div onClick={e => e.stopPropagation()}>
          {rule.trigger_type === "scheduled" && rule.enabled && (
            <button onClick={handleTrigger} disabled={triggering} data-testid={`trigger-btn-${rule.id}`}
              style={{ display: "flex", alignItems: "center", gap: 4, background: triggering ? "#94a3b8" : "#2563eb", color: "white", border: "none", borderRadius: 6, padding: "6px 10px", fontSize: 12, cursor: triggering ? "wait" : "pointer", fontWeight: 500 }}>
              <Play size={12} /> {triggering ? "..." : "Run"}
            </button>
          )}
        </div>
        <div onClick={e => e.stopPropagation()}>
          <button onClick={() => onToggle(rule.id, !rule.enabled)} data-testid={`toggle-btn-${rule.id}`}
            style={{ width: 44, height: 24, borderRadius: 12, border: "none", background: rule.enabled ? "#22c55e" : "#d1d5db", position: "relative", cursor: "pointer", transition: "background 0.2s" }}>
            <div style={{ width: 18, height: 18, borderRadius: 9, background: "white", position: "absolute", top: 3, left: rule.enabled ? 23 : 3, transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }} />
          </button>
        </div>
      </div>
    </div>
  );
}

/* ============ MAIN PAGE ============ */
export default function EmailRulesPage() {
  const { token } = useAuth();
  const [rules, setRules] = useState([]);
  const [stats, setStats] = useState({ total: 0, enabled: 0, disabled: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [toastMsg, setToastMsg] = useState(null);
  const [selectedRule, setSelectedRule] = useState(null);

  const showToast = (msg, type = "success") => { setToastMsg({ msg, type }); setTimeout(() => setToastMsg(null), 3000); };

  const fetchRules = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/email-rules`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const data = await res.json(); setRules(data.rules || []); setStats(data.stats || {}); }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchRules(); }, [fetchRules]);

  const handleToggle = async (ruleId, enabled) => {
    try {
      const res = await fetch(`${API}/api/email-rules/${ruleId}`, { method: "PATCH", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }, body: JSON.stringify({ enabled }) });
      if (res.ok) {
        setRules(prev => prev.map(r => r.id === ruleId ? { ...r, enabled } : r));
        setStats(prev => ({ ...prev, enabled: prev.enabled + (enabled ? 1 : -1), disabled: prev.disabled + (enabled ? -1 : 1) }));
        showToast(`Rule ${enabled ? "enabled" : "disabled"}`);
      }
    } catch (e) { showToast("Failed to update", "error"); }
  };

  const handleTrigger = async (ruleId) => {
    try {
      const res = await fetch(`${API}/api/email-rules/${ruleId}/trigger`, { method: "POST", headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (data.success) { showToast("Rule triggered successfully"); fetchRules(); }
      else showToast(data.message || "Trigger failed", "error");
    } catch (e) { showToast("Trigger failed", "error"); }
  };

  const handleSaveRule = async (ruleId, updates) => {
    try {
      const res = await fetch(`${API}/api/email-rules/${ruleId}`, { method: "PATCH", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }, body: JSON.stringify(updates) });
      if (res.ok) {
        showToast("Rule updated");
        fetchRules();
        setSelectedRule(prev => prev ? { ...prev, ...updates } : null);
      }
    } catch (e) { showToast("Save failed", "error"); }
  };

  const filtered = rules.filter(r => {
    if (searchTerm && !r.name.toLowerCase().includes(searchTerm.toLowerCase()) && !r.description?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (filterCategory !== "all" && r.category !== filterCategory) return false;
    if (filterStatus === "enabled" && !r.enabled) return false;
    if (filterStatus === "disabled" && r.enabled) return false;
    if (filterStatus === "error" && r.last_run_status !== "error") return false;
    return true;
  });

  const grouped = {};
  filtered.forEach(r => { const cat = r.category || "other"; if (!grouped[cat]) grouped[cat] = []; grouped[cat].push(r); });
  const categories = Object.keys(CATEGORY_CONFIG);

  return (
    <>
      <Navbar />
      <main style={{ maxWidth: 1200, margin: "0 auto", padding: "80px 20px 40px" }}>
        {toastMsg && <div style={{ position: "fixed", top: 80, right: 20, zIndex: 9999, background: toastMsg.type === "error" ? "#fee2e2" : "#dcfce7", color: toastMsg.type === "error" ? "#991b1b" : "#166534", padding: "12px 20px", borderRadius: 8, fontSize: 14, fontWeight: 500, boxShadow: "0 4px 12px rgba(0,0,0,0.15)" }}>{toastMsg.msg}</div>}

        {selectedRule && <RuleDetailModal rule={selectedRule} onClose={() => setSelectedRule(null)} onSave={handleSaveRule} token={token} />}

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <div>
            <h1 style={{ fontSize: 24, fontWeight: 700, color: "#0f172a", margin: 0 }} data-testid="email-rules-title">
              <Mail size={24} style={{ marginRight: 8, verticalAlign: "middle", color: "#2563eb" }} />Email Automation Control Center
            </h1>
            <p style={{ color: "#64748b", fontSize: 14, marginTop: 4 }}>Click any rule to view template, edit schedule, and see execution history</p>
          </div>
          <button onClick={fetchRules} style={{ display: "flex", alignItems: "center", gap: 6, background: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: 8, padding: "8px 16px", cursor: "pointer", fontSize: 13, fontWeight: 500 }} data-testid="refresh-btn"><RefreshCw size={14} /> Refresh</button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 24 }}>
          {[
            { val: stats.total, label: "Total Rules", color: "#2563eb", id: "stat-total" },
            { val: stats.enabled, label: "Active", color: "#22c55e", id: "stat-enabled" },
            { val: stats.disabled, label: "Disabled", color: "#f59e0b", id: "stat-disabled" },
            { val: rules.filter(r => r.last_run_status === "error").length, label: "Errors", color: "#dc2626", id: "stat-errors" },
          ].map(s => (
            <div key={s.id} style={{ background: "white", borderRadius: 10, padding: "16px 20px", boxShadow: "0 1px 3px rgba(0,0,0,0.06)", borderTop: `3px solid ${s.color}` }} data-testid={s.id}>
              <div style={{ fontSize: 28, fontWeight: 700, color: s.color }}>{s.val}</div>
              <div style={{ fontSize: 13, color: "#64748b" }}>{s.label}</div>
            </div>
          ))}
        </div>

        <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
          <div style={{ position: "relative", flex: 1, minWidth: 200 }}>
            <Search size={16} style={{ position: "absolute", left: 10, top: 10, color: "#94a3b8" }} />
            <input type="text" placeholder="Search rules..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{ width: "100%", padding: "8px 12px 8px 34px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, outline: "none" }} data-testid="search-input" />
          </div>
          <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)} style={{ padding: "8px 12px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, background: "white" }} data-testid="filter-category">
            <option value="all">All Categories</option>
            {categories.map(c => <option key={c} value={c}>{CATEGORY_CONFIG[c].label}</option>)}
          </select>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ padding: "8px 12px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, background: "white" }} data-testid="filter-status">
            <option value="all">All Statuses</option>
            <option value="enabled">Active Only</option>
            <option value="disabled">Disabled Only</option>
            <option value="error">Errors Only</option>
          </select>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 140px 180px 100px 90px 70px", padding: "8px 18px", fontSize: 11, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px", borderBottom: "2px solid #e2e8f0", marginBottom: 8 }}>
          <div>Rule</div><div>Category</div><div>Schedule</div><div>Last Run</div><div>Trigger</div><div>Active</div>
        </div>

        {loading ? <div style={{ textAlign: "center", padding: 40, color: "#94a3b8" }}>Loading...</div>
        : filtered.length === 0 ? <div style={{ textAlign: "center", padding: 40, color: "#94a3b8" }}>No rules match your filters</div>
        : Object.entries(grouped).sort(([a], [b]) => categories.indexOf(a) - categories.indexOf(b)).map(([cat, catRules]) => (
            <div key={cat} style={{ marginBottom: 24 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                {React.createElement(CATEGORY_CONFIG[cat]?.icon || Settings, { size: 16, style: { color: CATEGORY_CONFIG[cat]?.color || "#64748b" } })}
                <span style={{ fontSize: 13, fontWeight: 700, color: CATEGORY_CONFIG[cat]?.color || "#64748b", textTransform: "uppercase", letterSpacing: "0.5px" }}>{CATEGORY_CONFIG[cat]?.label || cat} ({catRules.length})</span>
              </div>
              {catRules.map(rule => <RuleRow key={rule.id} rule={rule} onToggle={handleToggle} onTrigger={handleTrigger} onOpenDetail={setSelectedRule} />)}
            </div>
          ))
        }
      </main>
    </>
  );
}
